import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  GraduationCap, Play, CheckCircle2, Lock, AlertTriangle, PhoneCall,
  Loader2, Award, ChevronRight, X, ArrowRight, Users,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DoozyGo® Academy — role-based onboarding (iter165).
 *
 * Flow:
 *   1. User picks a role (CRM / Ops / Finance / Sales / Manager)
 *   2. "Launch Walkthrough" spotlights each sidebar tab in sequence with a
 *      tooltip explaining what it does
 *   3. SOP cards (step-by-step + common mistakes + when to escalate)
 *   4. Mini-quiz (5 Qs, pass @ 80%) → earns a Certified badge
 *   5. Progress persists per user via /api/academy/*
 *
 * The spotlight tour is a tiny hand-rolled overlay (no react-joyride dep)
 * that reads `data-testid` targets from the sidebar.
 */

// ─── Spotlight Tour (hand-rolled, zero deps) ───────────────────────────
const SpotlightTour = ({ steps, onDone, onSaveProgress }) => {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);

  const current = steps[idx];

  useEffect(() => {
    if (!current) return undefined;
    const el = document.querySelector(current.target);
    if (!el) { setRect({ top: 100, left: 100, width: 300, height: 60, missing: true }); return undefined; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    return undefined;
  }, [current]);

  if (!current || !rect) return null;

  const tooltipTop = rect.top + rect.height + 12;
  const tooltipLeft = Math.min(rect.left, window.innerWidth - 340);

  const handleNext = () => {
    if (idx === steps.length - 1) {
      onSaveProgress?.({ tour_completed: true });
      onDone?.();
    } else {
      setIdx(idx + 1);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000]" data-testid="academy-spotlight">
      {/* Darken overlay with cutout */}
      <div className="absolute inset-0 bg-black/60 pointer-events-auto" onClick={onDone} />
      {!rect.missing && (
        <div
          className="absolute rounded-xl pointer-events-none ring-4 ring-[#DC283C] shadow-[0_0_0_9999px_rgba(0,0,0,0.6)]"
          style={{
            top: rect.top - 4, left: rect.left - 4,
            width: rect.width + 8, height: rect.height + 8,
          }}
        />
      )}
      {/* Tooltip */}
      <div
        className="absolute bg-white rounded-xl shadow-2xl p-4 w-[320px]"
        style={{ top: tooltipTop, left: tooltipLeft }}
        data-testid="academy-spotlight-tooltip"
      >
        <div className="flex items-start justify-between mb-2">
          <span className="text-[10px] font-bold text-[#DC283C] uppercase tracking-wider">
            Step {idx + 1} / {steps.length}
          </span>
          <button onClick={onDone} className="text-gray-400 hover:text-gray-600" data-testid="academy-spotlight-close">
            <X size={14} />
          </button>
        </div>
        <h3 className="font-bold text-[#1E1B4B] text-sm mb-1">{current.title}</h3>
        <p className="text-xs text-gray-600 leading-relaxed mb-4">{current.body}</p>
        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            {steps.map((step, i) => (
              <span
                key={`dot-${step.target}-${i}`}
                className={`w-1.5 h-1.5 rounded-full ${i === idx ? "bg-[#DC283C]" : "bg-gray-300"}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {idx > 0 && (
              <button onClick={() => setIdx(idx - 1)} className="text-[11px] text-gray-500 hover:text-gray-700" data-testid="academy-spotlight-back">
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#1E1B4B] text-white text-[11px] font-bold hover:bg-[#2d2a6e]"
              data-testid="academy-spotlight-next"
            >
              {idx === steps.length - 1 ? "Finish" : "Next"} <ArrowRight size={10} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Quiz Component ────────────────────────────────────────────────────
const Quiz = ({ questions, onSubmit, submitting }) => {
  const [answers, setAnswers] = useState(() => questions.map(() => -1));

  const allAnswered = answers.every(a => a >= 0);

  return (
    <div className="space-y-5" data-testid="academy-quiz">
      {questions.map((q, i) => (
        <div key={`q-${q.q.slice(0, 24)}-${i}`} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <p className="text-sm font-semibold text-[#1E1B4B] mb-3">
            Q{i + 1}. {q.q}
          </p>
          <div className="space-y-2">
            {q.options.map((opt, oi) => {
              const selected = answers[i] === oi;
              return (
                <button
                  key={`opt-${i}-${opt.slice(0, 16)}`}
                  onClick={() => {
                    const next = [...answers]; next[i] = oi; setAnswers(next);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-xs border transition-all ${
                    selected ? "border-[#DC283C] bg-[#DC283C]/5 text-[#1E1B4B] font-semibold"
                             : "border-gray-200 text-gray-700 hover:border-[#DC283C]/30"
                  }`}
                  data-testid={`academy-quiz-${i}-opt-${oi}`}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <button
        onClick={() => onSubmit(answers)}
        disabled={!allAnswered || submitting}
        className="w-full py-3 rounded-xl bg-gradient-to-r from-[#DC283C] to-indigo-500 text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="academy-quiz-submit"
      >
        {submitting ? <Loader2 className="animate-spin" size={16} /> : <>Submit Quiz <ChevronRight size={14} /></>}
      </button>
    </div>
  );
};

// ─── Main Academy Component ────────────────────────────────────────────
const DoozyAcademy = ({ authAxios }) => {
  const [paths, setPaths] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [curriculum, setCurriculum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTour, setShowTour] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [myProgress, setMyProgress] = useState({ progress: [], badges: [] });

  const fetchAll = useCallback(async () => {
    try {
      const [paR, meR] = await Promise.all([
        authAxios.get(`${API_URL}/api/academy/paths`),
        authAxios.get(`${API_URL}/api/academy/progress/me`),
      ]);
      setPaths(paR.data.paths || []);
      setMyProgress(meR.data || { progress: [], badges: [] });
    } catch (e) { console.debug("[Academy] fetch", e?.message); }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const loadCurriculum = async (role) => {
    setSelectedRole(role);
    setCurriculum(null);
    setQuizResult(null);
    try {
      const r = await authAxios.get(`${API_URL}/api/academy/paths/${role}`);
      setCurriculum(r.data);
    } catch (e) { console.debug("[Academy] load", e?.message); }
  };

  const saveProgress = async (patch) => {
    if (!selectedRole) return;
    try { await authAxios.post(`${API_URL}/api/academy/progress/${selectedRole}`, patch); }
    catch (e) { console.debug("[Academy] progress save", e?.message); }
  };

  const submitQuiz = async (answers) => {
    setSubmittingQuiz(true);
    try {
      const r = await authAxios.post(`${API_URL}/api/academy/quiz/${selectedRole}/submit`, { answers });
      setQuizResult(r.data);
      fetchAll();
    } catch (e) { console.debug("[Academy] submit", e?.message); }
    setSubmittingQuiz(false);
  };

  const progressMap = useMemo(() => {
    const m = {};
    (myProgress.progress || []).forEach(p => { m[p.role_path] = p; });
    return m;
  }, [myProgress]);

  const badgeSet = useMemo(() => new Set((myProgress.badges || []).map(b => b.role_path)), [myProgress]);

  if (loading) {
    return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-[#DC283C]" size={24} /></div>;
  }

  // Role picker view (no role selected)
  if (!selectedRole || !curriculum) {
    return (
      <div className="space-y-6" data-testid="academy-root">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-['Outfit'] text-2xl font-bold text-[#1E1B4B] flex items-center gap-2">
              <GraduationCap className="text-[#DC283C]" size={26} /> DoozyGo® Academy
            </h2>
            <p className="text-sm text-[#475569] mt-1">
              Pick your role → learn the dashboard end-to-end in 15 minutes → earn your Certified badge.
            </p>
          </div>
          {myProgress.badges?.length > 0 && (
            <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-800 px-3 py-1.5 rounded-full text-xs font-semibold border border-amber-200" data-testid="academy-my-badges">
              <Award size={14} /> {myProgress.badges.length} badge{myProgress.badges.length > 1 ? "s" : ""} earned
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paths.map(p => {
            const earned = badgeSet.has(p.role);
            const started = !!progressMap[p.role];
            return (
              <button
                key={p.role}
                onClick={() => loadCurriculum(p.role)}
                className="text-left bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-lg hover:border-[#DC283C]/30 transition-all"
                data-testid={`academy-role-${p.role}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#DC283C]/10 to-indigo-500/10 flex items-center justify-center">
                    <Users size={18} className="text-[#DC283C]" />
                  </div>
                  {earned && (
                    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full text-[10px] font-bold">
                      <CheckCircle2 size={10} /> Certified
                    </span>
                  )}
                  {!earned && started && (
                    <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full text-[10px] font-bold">
                      In Progress
                    </span>
                  )}
                </div>
                <h3 className="font-bold text-[#1E1B4B] text-sm mb-1">{p.label}</h3>
                <p className="text-xs text-[#475569] leading-relaxed line-clamp-3">{p.intro}</p>
                <div className="flex items-center gap-3 mt-3 text-[10px] text-gray-500">
                  <span>🎯 {p.tour_steps} tour steps</span>
                  <span>📘 {p.sop_cards} SOPs</span>
                  <span>✅ {p.quiz_questions} Qs quiz</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Curriculum view
  return (
    <>
      {showTour && (
        <SpotlightTour
          steps={curriculum.tour}
          onDone={() => setShowTour(false)}
          onSaveProgress={saveProgress}
        />
      )}

      <div className="space-y-6" data-testid="academy-curriculum">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <button
            onClick={() => { setSelectedRole(""); setCurriculum(null); setQuizResult(null); }}
            className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
            data-testid="academy-back-to-roles"
          >
            ← All roles
          </button>
          {badgeSet.has(selectedRole) && (
            <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold">
              <Award size={12} /> Certified
            </span>
          )}
        </div>

        <div className="bg-gradient-to-br from-[#1E1B4B] to-indigo-800 text-white rounded-2xl p-6">
          <h2 className="font-['Outfit'] text-2xl font-bold mb-2">{curriculum.label}</h2>
          <p className="text-white/80 text-sm leading-relaxed">{curriculum.intro}</p>
          <button
            onClick={() => setShowTour(true)}
            className="mt-4 inline-flex items-center gap-2 bg-[#DC283C] hover:bg-[#d33a7f] px-4 py-2.5 rounded-full text-sm font-bold shadow-lg transition-all"
            data-testid="academy-start-tour"
          >
            <Play size={14} /> Start Dashboard Walkthrough
          </button>
        </div>

        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">
            📘 Standard Operating Procedures
          </h3>
          <div className="space-y-3">
            {curriculum.sops.map((sop, i) => (
              <details
                key={`sop-${sop.title}-${i}`}
                className="bg-white rounded-xl border border-gray-100 shadow-sm group"
                data-testid={`academy-sop-${i}`}
                onToggle={(e) => { if (e.target.open) saveProgress({ step_index: i }); }}
              >
                <summary className="flex items-center justify-between px-4 py-3 cursor-pointer list-none">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#DC283C]/10 text-[#DC283C] text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    <span className="font-semibold text-sm text-[#1E1B4B]">{sop.title}</span>
                  </div>
                  <ChevronRight size={14} className="text-gray-400 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-4 pb-4 pt-1 space-y-3">
                  <div>
                    <p className="text-[10px] font-bold uppercase text-emerald-700 mb-1">Steps</p>
                    <ol className="list-decimal list-inside text-xs text-gray-700 space-y-1">
                      {sop.steps.map((s, si) => <li key={`step-${sop.title}-${si}`}>{s}</li>)}
                    </ol>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase text-red-700 mb-1 flex items-center gap-1">
                      <AlertTriangle size={10} /> Common mistakes
                    </p>
                    <ul className="list-disc list-inside text-xs text-gray-700 space-y-1">
                      {sop.mistakes.map((m, mi) => <li key={`miss-${sop.title}-${mi}`}>{m}</li>)}
                    </ul>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <p className="text-[10px] font-bold uppercase text-amber-800 mb-1 flex items-center gap-1">
                      <PhoneCall size={10} /> When to escalate
                    </p>
                    <p className="text-xs text-amber-900">{sop.escalate}</p>
                  </div>
                </div>
              </details>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-2">
            ✅ Final Quiz — pass with 80%+ to earn your badge
          </h3>
          {quizResult ? (
            <div className={`rounded-2xl p-6 text-center border ${quizResult.passed ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`} data-testid="academy-quiz-result">
              {quizResult.passed ? (
                <>
                  <Award size={48} className="text-emerald-500 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-emerald-700">{quizResult.score}%</p>
                  <p className="text-sm text-emerald-800 mt-1 font-semibold">Certified — welcome aboard!</p>
                  <p className="text-xs text-gray-600 mt-2">{quizResult.correct} / {quizResult.total} correct</p>
                </>
              ) : (
                <>
                  <Lock size={48} className="text-rose-400 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-rose-700">{quizResult.score}%</p>
                  <p className="text-sm text-rose-800 mt-1 font-semibold">Not yet — review the SOPs and try again.</p>
                  <p className="text-xs text-gray-600 mt-2">
                    You need {quizResult.pass_threshold}% · you got {quizResult.correct} / {quizResult.total}
                  </p>
                  <button
                    onClick={() => setQuizResult(null)}
                    className="mt-4 px-4 py-2 bg-rose-500 text-white rounded-full text-xs font-bold hover:bg-rose-600"
                    data-testid="academy-quiz-retake"
                  >
                    Try the quiz again
                  </button>
                </>
              )}
            </div>
          ) : (
            <Quiz questions={curriculum.quiz} onSubmit={submitQuiz} submitting={submittingQuiz} />
          )}
        </div>
      </div>
    </>
  );
};

export default DoozyAcademy;
